<template>
  <div ref="content" class="article-table__wrapper">
    <h3 v-if="tableName" class="article-table__table-name">
      {{ tableName }}
    </h3>
    <horizontal-scroll-wrapper
      v-if="columns.length"
      ref="scroll"
      :centered="false"
    >
      <a-scrollable-content
        :vertical="false"
        :horizontal="$_hydrationHelpers_isLayoutMobile"
        class="article-table__scrollable-content"
      >
        <div class="article-table__relative">
          <table class="article-table__table">
            <thead>
              <tr>
                <th
                  v-for="(column, index) in columns"
                  :key="index"
                  :style="dynamicColumnStyle[index]"
                  class="article-table__header-item"
                >
                  <h3 class="article-table__header-name">
                    {{ column.name }}
                  </h3>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="{ rowId, columns } in sortedRows"
                :key="rowId"
                class="article-table__row text-caption"
              >
                <td
                  v-for="{ colId, value } in columns"
                  :key="colId"
                  class="article-table__cell"
                  :style="dynamicColumnStyle[colId]"
                >
                  <component
                    :is="cellSettings[rowId][colId].component"
                    v-bind="cellSettings[rowId][colId].props"
                    class="article-table__cell-content"
                  >
                    {{ value }}
                  </component>
                </td>
              </tr>
              <tr
                v-if="isExpandVisible"
                class="article-table__row expand-section"
              >
                <td
                  :colspan="columns.length"
                  class="article-table__expand-cell"
                >
                  <span class="article-table__collapsed-row-count"
                    >Showing 50 of {{ rows.length }} entries</span
                  >
                  <button
                    :button-style="$options.consts.BUTTON_STYLE.NO_STYLE"
                    class="article-table__expand-button"
                    @click="expandTable"
                  >
                    Show All Entries
                  </button>
                </td>
              </tr>
            </tbody>
          </table>

          <!-- This block is hidden and is used only to get column width in DOM -->
          <div
            v-if="isCalculating"
            class="article-table__hidden-for-calculation"
          >
            <table class="article-table__table">
              <thead>
                <tr>
                  <th
                    ref="column"
                    v-for="(column, index) in columns"
                    :key="index"
                    class="article-table__header-item"
                  >
                    <h3 class="article-table__header-name">
                      {{ column.name }}
                    </h3>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="{ rowId, columns } in sortedRows"
                  :key="rowId"
                  class="article-table__row text-caption"
                >
                  <td
                    v-for="{ colId, value } in columns"
                    :key="colId"
                    :style="dynamicColumnStyle[colId]"
                    class="article-table__cell"
                  >
                    <component
                      :is="cellSettings[rowId][colId].component"
                      v-bind="cellSettings[rowId][colId].props"
                    >
                      {{ value }}
                    </component>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </a-scrollable-content>
    </horizontal-scroll-wrapper>
  </div>
</template>

<script>
import { url } from 'vuelidate/lib/validators'
import { isNil, pathOr } from 'ramda'

import mixins from '@/utils/mixins'
import { propValidator, PROP_TYPES, https } from '@fmpedia/validators'
import HorizontalScrollWrapper from 'shared/AMakeYourAccountBetter/_shared/HorizontalScrollWrapper'
import AScrollableContent from 'shared/AScrollableContent'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import {
  BASE_HTML_REL_ATTRIBUTE_VALUES,
  HTML_REL_ATTRIBUTE_VALUE
} from '@fmpedia/enums'
import { LinkRelAttributeConverter } from '@fmpedia/html-tools'
import { BUTTON_STYLE } from 'shared/AButton'
import { decodeAttributeQuotes } from '@/utils/helpers/encodeDecodeAttributeQuotes'

export const SORT_ORDER = {
  ASC: 'asc',
  DESC: 'desc'
}

export const DATA_TYPE = {
  TEXT: 'TEXT',
  NUMBER: 'NUMBER',
  DATE: 'DATE',
  LINK: 'LINK',
  CURRENCY: 'CURRENCY',
  SPECIAL_TEXT: 'SPECIAL_TEXT',
  SPECIAL_NUMBER: 'SPECIAL_NUMBER'
}

const SORT_VALUE = {
  [SORT_ORDER.ASC]: {
    aGreaterB: 1,
    aLessB: -1
  },
  [SORT_ORDER.DESC]: {
    aGreaterB: -1,
    aLessB: 1
  }
}

const SHIFT_DIRECTION = {
  LEFT: 'LEFT',
  RIGHT: 'RIGHT'
}

const n = 1.1
const LOCAL_DECIMAL_SEPARATOR = n.toLocaleString().substring(1, 2)

function getNumber(rawValue) {
  try {
    /**
     * This regex tries to get float number from any string with any decimal separator (. or ,)
     * @type {RegExp}
     */
    const regEx = /[-]{0,1}[\d]*[.,]{0,1}[\d]+/g

    const localNumber = rawValue.replace(/,./g, LOCAL_DECIMAL_SEPARATOR)
    const number = localNumber.match(regEx)[0]

    if (Number.isNaN(Number.parseFloat(number))) {
      return null
    }
    return parseFloat(number)
  } catch (err) {
    /* No need to catch error inside this function */
    return null
  }
}

const VALUE_PARSER_BY_TYPE = {
  [DATA_TYPE.TEXT]: rawValue => rawValue,
  [DATA_TYPE.LINK]: rawValue => rawValue,
  [DATA_TYPE.SPECIAL_TEXT]: rawValue => rawValue,
  [DATA_TYPE.NUMBER]: rawValue => getNumber(rawValue),
  [DATA_TYPE.CURRENCY]: rawValue => getNumber(rawValue),
  [DATA_TYPE.SPECIAL_NUMBER]: rawValue => getNumber(rawValue),
  [DATA_TYPE.DATE]: rawValue => {
    try {
      const splittedDate = rawValue.split('.')
      if (
        splittedDate.length === 3 &&
        splittedDate.every(number => parseInt(number))
      ) {
        const [date, month, year] = splittedDate
        return new Date(year, month, date)
      }

      return null
    } catch (err) {
      /* No need to catch this error */
      return null
    }
  }
}

const COLLAPSED_ITEM_COUNT = 50

export default {
  name: 'ATableInsideArticle',
  mixins: [mixins.refDimensions, hydrationHelpers],
  components: { HorizontalScrollWrapper, AScrollableContent },
  props: {
    tableData: propValidator([PROP_TYPES.STRING], false)
  },
  consts: { BUTTON_STYLE },
  data() {
    return {
      tableName: '',
      columns: [],
      rows: [],
      columnIndexToSort: null,
      sortOrder: SORT_ORDER.ASC,
      columnWidthArray: [],
      isCalculating: true,
      isExpanded: false
    }
  },
  computed: {
    isExpandVisible() {
      return !this.isExpanded && this.rows.length > COLLAPSED_ITEM_COUNT
    },
    sortedRows() {
      const sortedRows = this.rows.map((row, rowIndex) => ({
        rowId: rowIndex,
        columns: row.map((col, colIndex) => ({ ...col, colId: colIndex }))
      }))

      if (!isNil(this.columnIndexToSort) && this.sortOrder) {
        sortedRows.sort((a, b) => {
          const aValue = this.getValueForSorting(a)
          const bValue = this.getValueForSorting(b)

          const { aLessB, aGreaterB } = SORT_VALUE[this.sortOrder]

          if (aValue == null) return aLessB
          if (bValue == null) return aGreaterB

          if (aValue > bValue) return aGreaterB

          if (aValue < bValue) return aLessB

          return 0
        })
      }

      return this.isExpandVisible
        ? sortedRows.slice(0, COLLAPSED_ITEM_COUNT)
        : sortedRows
    },
    contentWidth() {
      return (
        (this.refDimensions.content && this.refDimensions.content.width) || 0
      )
    },
    columnWidthLimit() {
      return {
        min: 100,
        max: Math.floor((this.contentWidth * 2) / 3)
      }
    },
    columnWidthArrayLimited() {
      return this.columnWidthArray.map(width => {
        if (width > this.columnWidthLimit.max) {
          return this.columnWidthLimit.max
        } else if (width < this.columnWidthLimit.min) {
          return this.columnWidthLimit.min
        } else {
          return width
        }
      })
    },
    dynamicColumnStyle() {
      /**
       * If there are no real column sizes we return empty styles
       */
      if (!this.columnWidthArray.every(width => width > 1)) {
        return this.columnWidthArray.map(() => ({}))
      }

      /**
       * Sum of all column widths
       */
      let widthSum = 0

      /**
       * Sum of column widths which are fully displayed on the screen
       * @type {number}
       */
      let widthSumOnScreen = 0

      /**
       * Column index which is partially shown on the screen
       * @type {null}
       */
      let borderedColumnIndex = null

      /**
       * We're calculating widthSum, widthSumOnScreen and borderedColumnIndex
       */
      this.columnWidthArrayLimited.forEach((width, index) => {
        widthSum += width
        if (widthSum > this.contentWidth) {
          if (borderedColumnIndex === null) {
            borderedColumnIndex = index
            widthSumOnScreen = widthSum - width
          }
        }
      })

      /**
       * If the whole table is visible on the screen we return empty styles
       */
      if (widthSum < this.contentWidth) {
        return this.columnWidthArray.map(() => ({}))
      }

      /**
       * Difference between screen width and visible column widths + 1/2 of bordered
       * column width (on how many pixels we should shift the table to get cell center
       * on content right border)
       * @type {number}
       */
      const middleCenterDiff = Math.floor(
        this.contentWidth -
          widthSumOnScreen -
          this.columnWidthArrayLimited[borderedColumnIndex] / 2
      )

      const shiftDirection =
        middleCenterDiff > 0 ? SHIFT_DIRECTION.RIGHT : SHIFT_DIRECTION.LEFT

      const shiftLeft = this.getShiftLeft(
        borderedColumnIndex,
        widthSumOnScreen,
        widthSum
      )

      const shiftRight = this.getShiftRight(
        borderedColumnIndex,
        widthSumOnScreen
      )

      const shiftPerColumnWithinCell = Math.floor(
        middleCenterDiff / borderedColumnIndex
      )

      const shiftSettings = this.getShiftSettings({
        shiftDirection,
        columnIndex: borderedColumnIndex,
        shiftPerColumn: shiftPerColumnWithinCell,
        shiftLeft,
        shiftRight
      })

      const newWidthArray = this.createNewWidthArray(shiftSettings)
      return newWidthArray.map(width => ({ width: `${width}px` }))
    },
    cellSettings() {
      return this.rows.map(row =>
        row.map((cell, columnIndex) => {
          const isTypeLink =
            this.columns[columnIndex].dataType === DATA_TYPE.LINK
          const link = pathOr(cell.value || '', ['options', 'link'], cell)
          if (isTypeLink && url(link) && https(link)) {
            return {
              component: 'a',
              props: {
                href: link,
                target: '_blank',
                rel: LinkRelAttributeConverter.generateRelString([
                  HTML_REL_ATTRIBUTE_VALUE.NOFOLLOW,
                  ...BASE_HTML_REL_ATTRIBUTE_VALUES
                ])
              },
              value: cell.value
            }
          } else {
            return {
              component: 'span',
              props: {},
              value: cell.value
            }
          }
        })
      )
    }
  },
  watch: {
    $_hydrationHelpers_isLayoutMobile: {
      handler() {
        this.recalculate()
      }
    }
  },
  methods: {
    setInitialData() {
      if (!this.tableData) return

      try {
        const tableData = decodeAttributeQuotes(this.tableData)

        const parsedTable = JSON.parse(tableData)
        if (parsedTable) {
          this.tableName = parsedTable.tableName
          this.columns = parsedTable.columns
          this.rows = parsedTable.rows
          this.columnIndexToSort = isNil(parsedTable.columnIndexToSort)
            ? null
            : parsedTable.columnIndexToSort
          this.sortOrder = parsedTable.sortOrder || SORT_ORDER.ASC
        } else {
          this.clearData()
        }
      } catch (err) {
        this.$errorHandler(err, this)
        this.clearData()
      }
    },
    clearData() {
      this.tableName = ''
      this.columns = []
      this.rows = []
      this.columnIndexToSort = null
      this.sortOrder = SORT_ORDER.ASC
    },
    async recalculate() {
      this.isCalculating = true
      await this.$nextTick()
      setTimeout(this.$_refDimensions_setDimensions, 0)
      this.$refs.scroll.getContentSizeWithScroll()
    },
    getValueForSorting(row) {
      const type = this.columns[this.columnIndexToSort].dataType
      const { value } = row.columns[this.columnIndexToSort]
      return VALUE_PARSER_BY_TYPE[type](value)
    },
    $_refDimensions_runOnChange() {
      if (
        !this.columns.length ||
        !this.isCalculating ||
        !this.columns.every(
          (_, index) =>
            this.refDimensions.column[index] &&
            this.refDimensions.column[index].width
        )
      ) {
        return
      }

      this.columnWidthArray = this.columns.map((_, index) => {
        return this.refDimensions.column[index].width + 1
      })
      this.isCalculating = false
      this.$refs.scroll.getContentSize()
    },
    isShiftPossible(array, shiftPerColumn) {
      const result = array.reduce((acc, width) => {
        const tempWidth = width + shiftPerColumn + acc
        if (tempWidth < this.columnWidthLimit.min) {
          return acc - (this.columnWidthLimit.min - tempWidth)
        }
        if (tempWidth > this.columnWidthLimit.max) {
          return acc + tempWidth - this.columnWidthLimit.max
        }
        return 0
      }, 0)

      return !result
    },
    getShiftLeft(columnIndex, widthSumOnScreen, totalWidth) {
      const nextCellWidth = Math.floor(
        columnIndex < this.columnWidthArrayLimited.length
          ? widthSumOnScreen +
              this.columnWidthArrayLimited[columnIndex] +
              this.columnWidthArrayLimited[columnIndex + 1] / 2
          : totalWidth
      )
      return this.contentWidth - nextCellWidth
    },
    getShiftRight(columnIndex, widthSumOnScreen) {
      const previousCellWidth = Math.floor(
        widthSumOnScreen - this.columnWidthArrayLimited[columnIndex - 1] / 2
      )
      return this.contentWidth - previousCellWidth
    },
    /**
     * We are trying to calculate if shift within bordered cell is possible.
     * In case we cannot shift left the table (reduce cell width) we will shift
     * right (enlarge cell width).
     * And vice versa:
     * If we cannot shift right the table (enlarge cell width) we will shift left
     * (reduce cell width)
     * @param shiftDirection  - LEFT or RIGHT
     * @param columnIndex     - index of the column which is partially fit the screen
     * @param shiftPerColumn  - how many px should we enlarge/reduce every column to fit cell center
     * @param shiftLeft       - shift outside scope of the bordered cell (bordered cell index + 1)
     * @param shiftRight      - shift outside scope of the bordered cell (bordered cell index - 1)
     * @returns {Object}      - returns shiftPerColumn in pixels and bordered column index
     */
    getShiftSettings({
      shiftDirection,
      columnIndex,
      shiftPerColumn,
      shiftLeft,
      shiftRight
    }) {
      const isShiftPossibleWithinCell = this.isShiftPossible(
        this.columnWidthArrayLimited.slice(0, columnIndex),
        shiftPerColumn
      )

      const shiftHandler = {
        [SHIFT_DIRECTION.LEFT]: isShiftPossibleWithinCell => ({
          shiftPerColumn: isShiftPossibleWithinCell
            ? shiftPerColumn
            : Math.floor(shiftRight / (columnIndex - 1)),
          shiftIndex: isShiftPossibleWithinCell ? columnIndex : columnIndex - 1
        }),
        [SHIFT_DIRECTION.RIGHT]: isShiftPossibleWithinCell => ({
          shiftPerColumn: isShiftPossibleWithinCell
            ? shiftPerColumn
            : Math.floor(shiftLeft / (columnIndex + 1)),
          shiftIndex: isShiftPossibleWithinCell ? columnIndex : columnIndex + 1
        })
      }

      return shiftHandler[shiftDirection](isShiftPossibleWithinCell)
    },
    createNewWidthArray(shiftSettings) {
      let acc = 0
      return this.columnWidthArrayLimited.map((width, index) => {
        if (index >= shiftSettings.shiftIndex) return width

        const tempWidth = width + shiftSettings.shiftPerColumn + acc

        if (tempWidth < this.columnWidthLimit.min) {
          acc = acc - (this.columnWidthLimit.min - tempWidth)
          return this.columnWidthLimit.min
        }
        if (tempWidth > this.columnWidthLimit.max) {
          acc = acc + tempWidth - this.columnWidthLimit.max
          return this.columnWidthLimit.max
        }

        acc = 0
        return tempWidth
      })
    },
    expandTable() {
      this.isExpanded = true
      this.$nextTick(this.recalculate)
    }
  },
  created() {
    this.setInitialData()
  }
}
</script>

<style lang="scss" scoped>
.article-table__wrapper {
  width: 100%;
  display: inline-block;

  .article-table__table-name {
    margin-top: 0;
    margin-bottom: 15px;
  }

  .article-table__scrollable-content {
    width: max-content; /* Should be fixed for Internet Explorer */

    @include mobile {
      width: 100%;
    }
  }

  .article-table__relative {
    position: relative;
  }

  .article-table__table {
    table-layout: fixed;
    margin-bottom: 10px;
    padding-right: 2px;
    max-width: none; // rewrite <table> tag styles
    width: auto; // rewrite <table> tag styles
    white-space: normal; // rewrite <table> tag styles

    @include mobile {
      padding-bottom: 5px;
    }
  }

  .article-table__header-item {
    height: 40px;
    min-width: 100px;
    max-width: percentage(2/3);
    padding: 4px 8px;
    background: $c--row-delimiter;
    text-align: center;
    border: 1px solid black;
    transition: width 0.3s ease-in-out;
    word-wrap: break-word;
  }

  .article-table__header-name {
    margin-top: 0;
    margin-bottom: 0;
  }

  .article-table__row {
    height: 30px;
  }

  .article-table__row.expand-section {
    height: auto;
    border: 1px dashed $c--black;

    .article-table__expand-cell {
      padding: 5px 8px;
    }

    .article-table__collapsed-row-count {
      font-size: 0.8rem;
      font-weight: 600;
      color: $c--gray;
    }

    .article-table__expand-button {
      color: $c--gray;
      display: inline-block;
      border: 1px solid $c--gray-light;
      padding: 5px 10px;
      cursor: pointer;
    }

    .article-table__expand-button:hover {
      color: $c--gray-main;
      border-color: $c--black;
    }
  }

  .article-table__cell {
    font-size: 16px;
    padding: 5px 8px;
    border: 1px solid $c--black;
    word-wrap: break-word;
  }

  .article-table__cell-content {
    word-wrap: break-word;
  }

  /deep/
    .a-scrollable-content__parent-wrapper
    .a-scrollable-content__content-wrapper {
    max-height: none;
  }

  .article-table__hidden-for-calculation {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    visibility: hidden;
  }
}
</style>
